import { useEffect, useMemo, ChangeEvent } from 'react'
import { Input, Select, SlimButton, Typography, UploadButton } from 'components'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useSelector, useDispatch } from 'store/store'
import { getReasonsJustification } from 'store/OVContext/ovSlice'
import * as yup from 'yup'
import * as S from './styles'
import { OVTypes } from 'constants/enums'

const schema = yup.object().shape({
  codReason: yup.string().required(),
  descReason: yup.string().required(),
  descRequest: yup.string().required(),
  file_name: yup.string().optional(),
  file: yup.mixed().optional()
})

export type JustifyNDFields = {
  codReason: string
  descReason: string
  descRequest: string
  file_name: string
  file: File
}

type JustifyNDFormProps = {
  setOpen: (open: boolean) => void
  sendAction: (data?: JustifyNDFields) => void
}

const JustifyNDForm = ({ setOpen, sendAction }: JustifyNDFormProps) => {
  const dispatch = useDispatch()
  const {
    ov: { reasonsJustification },
    salesOrder: { draftBody }
  } = useSelector((state) => state)

  const defaultValues = {
    codReason: '',
    descReason: '',
    descRequest: '',
    file_name: '',
    file: new File([], '')
  }
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isValid }
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  const reasonOptions = useMemo(() => {
    return reasonsJustification
      .filter((item) => {
        if (draftBody.cd_tipo_orde !== OVTypes.YRBA) {
          return (
            item.CodigoMotivoNegociacao !== '15' && item.CodigoMotivoNegociacao !== '12'
          )
        }
        return true
      })
      .map((reason) => ({
        value: reason.CodigoMotivoNegociacao,
        title: reason.DescricaoMotivoNegociacao
      }))
  }, [reasonsJustification])

  useEffect(() => {
    if (draftBody.cd_tipo_orde === OVTypes.YRBA) {
      const yrbaCode = '12'
      const yrbaReason = reasonOptions.find((item) => item.value === yrbaCode)
      setValue('codReason', yrbaCode)
      setValue('descReason', yrbaReason?.title as string)
    }
  }, [draftBody.cd_tipo_orde, reasonOptions])

  useEffect(() => dispatch(getReasonsJustification()), [])

  const handleSendSubmit = (data: JustifyNDFields) => {
    sendAction(data)
  }

  return (
    <S.Form onSubmit={handleSubmit(handleSendSubmit)}>
      <S.FieldsContainer>
        <Typography design="hav-default-03-text">
          A seleção para envio contém uma ou mais OVs identificadas como Negociação
          diferenciada (ND). É necessário justificá-las antes do envio.
        </Typography>

        <div>
          <Controller
            name={'codReason'}
            control={control}
            render={({ field: { name, onChange, value } }) => (
              <Select
                label="Motivo da solicitação *"
                name={name}
                value={value}
                options={reasonOptions}
                menuText={
                  !reasonOptions.length ? 'Carregando opções...' : 'Selecione o motivo'
                }
                onChange={(e) => {
                  const reason = reasonOptions.find((r) => r.value === e.target.value)
                  reason && setValue('descReason', reason?.title)
                  onChange(e.target.value)
                }}
                disabled={
                  !reasonOptions.length || draftBody.cd_tipo_orde === OVTypes.YRBA
                }
                optionsMaxHeight={200}
              />
            )}
          />
        </div>
        <div>
          <Controller
            name="descRequest"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                value={value}
                onChange={onChange}
                label="Descrição da solicitação"
                height={90}
                rows={3}
                InputProps={{
                  placeholder: 'Digite aqui mais informações sobre a solicitação',
                  inputProps: { maxLength: 264 }
                }}
                required
              />
            )}
          />
        </div>
        <S.UploadContainer>
          <Controller
            name="file"
            control={control}
            render={({ field: { onChange, value } }) => (
              <UploadButton
                key={`fileInput`}
                value={value as File}
                onClear={() => {
                  setValue('file', new File([], ''))
                  setValue('file_name', '')
                }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.files?.length) {
                    onChange(e.target.files[0])
                    setValue(`file_name`, e.target.files[0].name, {
                      shouldDirty: true
                    })
                  }
                }}
                id="fileInput"
                name="upload"
                placeholder="Clique aqui para selecionar arquivo"
              />
            )}
          />
        </S.UploadContainer>
      </S.FieldsContainer>

      <S.Footer>
        <SlimButton
          content="Voltar"
          type="button"
          variant="secondary"
          onClick={() => setOpen(false)}
        />
        <SlimButton
          content="Enviar"
          type="submit"
          variant="primary"
          disabled={!isValid}
        />
      </S.Footer>
    </S.Form>
  )
}

export default JustifyNDForm
